import { SenderEmailWarning } from "components/SenderEmailWarning";
import { Button } from "components/ui/Button";
import { Loading } from "components/ui/Loading/Loading";
import { Select } from "components/ui/Select/Select";
import type {
  PromptType,
  Recipient,
  RecipientType,
  ReplyDraft,
} from "hooks/use-email-compose";
import { useSenderEmails } from "hooks/use-sender-emails-api";
import { type FC, useState } from "react";
import { MarginSelect } from "routes/emails/EmailDetailPage/components/MarginSelect";
import {
  ProposalInfoInput,
  type ProposalInfoTarget,
} from "routes/emails/EmailDetailPage/components/ProposalInfoInput";
import { ReplyConfirmModal } from "routes/emails/EmailDetailPage/components/ReplyConfirmModal";

// biome-ignore lint/style/useNamingConvention: API prompt_type values
const PROMPT_TYPE_LABELS: Record<PromptType, string> = {
  reply_matter: "人材を提案",
  reply_hr: "案件を提案",
  inquiry: "詳細問い合わせ",
  negotiate: "単価交渉",
  schedule: "日程調整",
  decline: "お断り",
  followup: "フォローアップ",
  custom: "カスタム指示",
};

const COMMON_PROMPT_TYPES: PromptType[] = ["inquiry", "negotiate", "followup"];

export type SourceEmailType = "hr" | "matter" | null;

export type ReplyComposePaneProps = {
  draft: ReplyDraft;
  sourceEmailType: SourceEmailType;
  isGenerating: boolean;
  isSending: boolean;
  error: string | null;
  onFieldChange: <K extends keyof ReplyDraft>(
    field: K,
    value: ReplyDraft[K],
  ) => void;
  onAddRecipient: (recipient: Recipient) => void;
  onRemoveRecipient: (email: string, type: RecipientType) => void;
  onGenerateBody: (promptType: PromptType, freeText?: string) => void;
  onSend: () => Promise<boolean>;
  onClose: () => void;
  onCancelGeneration: () => void;
};

/**
 * 返信メール作成ペイン
 * @param {ReplyComposePaneProps} props
 * @return {JSX.Element}
 */
export const ReplyComposePane: FC<ReplyComposePaneProps> = ({
  draft,
  sourceEmailType,
  isGenerating,
  isSending,
  error,
  onFieldChange,
  onAddRecipient,
  onRemoveRecipient,
  onGenerateBody,
  onSend,
  onClose,
  onCancelGeneration,
}) => {
  const [showConfirm, setShowConfirm] = useState(false);
  const [showCcBcc, setShowCcBcc] = useState(false);
  const [newCcEmail, setNewCcEmail] = useState("");
  const contextReplyType: PromptType =
    sourceEmailType === "hr" ? "reply_hr" : "reply_matter";
  const availablePromptTypes: PromptType[] =
    sourceEmailType === null
      ? ["reply_matter", "reply_hr", ...COMMON_PROMPT_TYPES]
      : [contextReplyType, ...COMMON_PROMPT_TYPES];
  const [selectedPromptType, setSelectedPromptType] =
    useState<PromptType>(contextReplyType);

  const isReplyWithProposal =
    selectedPromptType === "reply_matter" || selectedPromptType === "reply_hr";
  const proposalTarget: ProposalInfoTarget =
    selectedPromptType === "reply_matter" ? "hr" : "matter";

  const { data: senderEmailsData } = useSenderEmails({ verifiedOnly: true });
  const senderEmails = senderEmailsData?.items ?? [];

  const hasSender = senderEmails.length > 0 || !!draft.fromEmail;
  const toRecipients = draft.recipients.filter(r => r.type === "to");
  const ccRecipients = draft.recipients.filter(r => r.type === "cc");
  const bccRecipients = draft.recipients.filter(r => r.type === "bcc");

  /**
   * CC/BCCアドレスを追加する
   * @param {RecipientType} type - 宛先種別
   * @return {void}
   */
  const handleAddCcBcc = (type: RecipientType) => {
    const trimmed = newCcEmail.trim();
    if (!trimmed) return;
    onAddRecipient({ email: trimmed, name: "", type });
    setNewCcEmail("");
  };

  /**
   * 送信確認を開く
   * @return {void}
   */
  const handleOpenConfirm = () => {
    setShowConfirm(true);
  };

  /**
   * 送信を実行
   * @return {Promise<void>}
   */
  const handleConfirmSend = async () => {
    setShowConfirm(false);
    await onSend();
  };

  /**
   * AI生成を実行
   * @return {void}
   */
  const handleGenerate = () => {
    onGenerateBody(selectedPromptType);
  };

  return (
    <div className="card bg-base-100 shadow-md">
      <div className="card-body space-y-3">
        {/* ヘッダー */}
        <div className="flex items-center justify-between">
          <h3 className="card-title text-base">返信メール作成</h3>
          <Button variant="ghost" size="xs" onClick={onClose} type="button">
            ✕
          </Button>
        </div>

        {/* エラー表示 */}
        {error && (
          <div className="alert alert-error text-sm py-2">
            <span>{error}</span>
          </div>
        )}

        {/* 送信元 */}
        <div>
          <label className="label py-0">
            <span className="label-text text-xs">送信元</span>
          </label>
          {senderEmails.length === 0 && !draft.fromEmail ? (
            <SenderEmailWarning />
          ) : (
            <Select
              options={
                senderEmails.length === 0 && draft.fromEmail
                  ? [
                      {
                        value: draft.fromEmail,
                        label: `${draft.fromName} (${draft.fromEmail})`,
                      },
                    ]
                  : senderEmails.map(s => ({
                      value: s.email,
                      label: `${s.displayName} (${s.email})`,
                    }))
              }
              value={draft.fromEmail}
              onChange={next => {
                if (!next) return;
                onFieldChange("fromEmail", next);
                const sender = senderEmails.find(s => s.email === next);
                if (sender) {
                  onFieldChange("fromName", sender.displayName);
                }
              }}
              size="sm"
            />
          )}
        </div>

        {/* 宛先 (To) */}
        <div>
          <label className="label py-0">
            <span className="label-text text-xs">宛先 (To)</span>
          </label>
          <div className="space-y-1">
            {toRecipients.map(r => (
              <div
                key={r.email}
                className="input input-bordered input-sm w-full !h-auto py-1 bg-gray-50 text-sm text-gray-600 leading-snug"
              >
                {r.name ? `${r.name} <${r.email}>` : r.email}
              </div>
            ))}
          </div>
          {/* CC/BCC トグル */}
          {!showCcBcc && (
            <div className="mt-1">
              <Button
                variant="ghost"
                size="xs"
                onClick={() => setShowCcBcc(true)}
                type="button"
              >
                + CC/BCC を追加
              </Button>
            </div>
          )}
        </div>

        {/* CC/BCC セクション */}
        {showCcBcc && (
          <>
            {/* CC */}
            <div>
              <label className="label py-0">
                <span className="label-text text-xs">CC</span>
              </label>
              <div className="space-y-1">
                {ccRecipients.map(r => (
                  <div key={r.email} className="flex items-center gap-1">
                    <span className="input input-bordered input-sm flex-1 min-w-0 !h-auto py-1 bg-gray-50 text-sm text-gray-600 leading-snug break-all">
                      {r.email}
                    </span>
                    <Button
                      variant="ghost"
                      size="xs"
                      onClick={() => onRemoveRecipient(r.email, "cc")}
                      type="button"
                    >
                      ✕
                    </Button>
                  </div>
                ))}
                <div className="flex gap-1">
                  <input
                    type="email"
                    className="input input-bordered input-sm flex-1"
                    placeholder="cc@example.com"
                    value={newCcEmail}
                    onChange={e => setNewCcEmail(e.target.value)}
                    onKeyDown={e => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        handleAddCcBcc("cc");
                      }
                    }}
                  />
                  <Button
                    variant="ghost"
                    size="sm"
                    shape="square"
                    onClick={() => handleAddCcBcc("cc")}
                    type="button"
                  >
                    +
                  </Button>
                </div>
              </div>
            </div>

            {/* BCC */}
            <div>
              <label className="label py-0">
                <span className="label-text text-xs">BCC</span>
              </label>
              <div className="space-y-1">
                {bccRecipients.map(r => (
                  <div key={r.email} className="flex items-center gap-1">
                    <span className="input input-bordered input-sm flex-1 min-w-0 !h-auto py-1 bg-gray-50 text-sm text-gray-600 leading-snug break-all">
                      {r.email}
                    </span>
                    <Button
                      variant="ghost"
                      size="xs"
                      onClick={() => onRemoveRecipient(r.email, "bcc")}
                      type="button"
                    >
                      ✕
                    </Button>
                  </div>
                ))}
                <div className="flex gap-1">
                  <input
                    type="email"
                    className="input input-bordered input-sm flex-1"
                    placeholder="bcc@example.com"
                    value={newCcEmail}
                    onChange={e => setNewCcEmail(e.target.value)}
                    onKeyDown={e => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        handleAddCcBcc("bcc");
                      }
                    }}
                  />
                  <Button
                    variant="ghost"
                    size="sm"
                    shape="square"
                    onClick={() => handleAddCcBcc("bcc")}
                    type="button"
                  >
                    +
                  </Button>
                </div>
              </div>
            </div>
          </>
        )}

        {/* 件名 */}
        <div>
          <label className="label py-0">
            <span className="label-text text-xs">件名</span>
          </label>
          <input
            type="text"
            className="input input-bordered input-sm w-full"
            value={draft.subject}
            onChange={e => onFieldChange("subject", e.target.value)}
          />
        </div>

        {/* AI生成セクション */}
        <div className="bg-blue-50 rounded-lg p-3 border border-blue-100 space-y-2">
          <div className="flex items-center gap-2">
            <span className="text-xs font-semibold text-blue-700">
              AI文面生成
            </span>
          </div>
          <Select
            options={availablePromptTypes.map(key => ({
              value: key,
              label: PROMPT_TYPE_LABELS[key],
            }))}
            value={selectedPromptType}
            onChange={value => {
              if (value) setSelectedPromptType(value as PromptType);
            }}
            size="sm"
          />
          {isReplyWithProposal && (
            <>
              <MarginSelect
                value={draft.margin}
                onChange={value => onFieldChange("margin", value)}
                disabled={isGenerating}
              />
              <ProposalInfoInput
                value={draft.proposalInfo}
                onChange={value => onFieldChange("proposalInfo", value)}
                target={proposalTarget}
                disabled={isGenerating}
              />
            </>
          )}
          <div className="flex justify-end pt-1">
            <Button
              variant="accent"
              size="sm"
              onClick={handleGenerate}
              disabled={isGenerating}
              type="button"
            >
              {isGenerating ? (
                <>
                  <Loading size="xs" />
                  生成中
                </>
              ) : (
                "生成"
              )}
            </Button>
          </div>
        </div>

        {/* 本文 */}
        <div className="flex-1">
          <label className="label py-0">
            <span className="label-text text-xs">
              本文
              {isGenerating && (
                <span className="ml-2 text-blue-500">
                  <span className="loading loading-dots loading-xs" />{" "}
                  AI生成中...
                </span>
              )}
            </span>
          </label>
          <textarea
            className="textarea textarea-bordered w-full text-sm"
            rows={12}
            value={draft.body}
            onChange={e => onFieldChange("body", e.target.value)}
            placeholder={
              isGenerating
                ? "AIがメール本文を生成しています..."
                : "本文を入力..."
            }
          />
        </div>

        {/* ボタン群 */}
        <div className="flex justify-between items-center">
          <Button variant="ghost" size="sm" onClick={onClose} type="button">
            キャンセル
          </Button>
          <div className="flex gap-2">
            {isGenerating && (
              <Button
                variant="ghost"
                size="sm"
                onClick={onCancelGeneration}
                type="button"
              >
                生成停止
              </Button>
            )}
            <Button
              size="sm"
              onClick={handleOpenConfirm}
              disabled={
                isGenerating || isSending || !draft.body.trim() || !hasSender
              }
              type="button"
            >
              {isSending ? (
                <>
                  <Loading size="xs" />
                  送信中...
                </>
              ) : (
                "送信"
              )}
            </Button>
          </div>
        </div>

        {/* 確認モーダル */}
        <ReplyConfirmModal
          isOpen={showConfirm}
          subject={draft.subject}
          body={draft.body}
          recipients={draft.recipients}
          fromName={draft.fromName}
          onConfirm={handleConfirmSend}
          onCancel={() => setShowConfirm(false)}
        />
      </div>
    </div>
  );
};
